// src/features/chales/hooks/useChalesData.js
import { useState, useEffect, useMemo } from "react";
// Removido import de launchChalesFinancials
import toast from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { db } from "services/firebaseConfig";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  addDoc,
  deleteDoc,
  Timestamp,
  getDocs,
  setDoc,
  getDoc,
} from "firebase/firestore";

const NUMBER_OF_CHALES = 10;

const cleanValue = (value) => {
  if (value === null || value === "" || value === undefined) return 0;
  if (typeof value === "number") return value;
  const cleanedString = String(value)
    .replace("R$", "")
    .replace(/\./g, "")
    .replace(",", ".")
    .trim();
  const number = parseFloat(cleanedString);
  return isNaN(number) ? 0 : number;
};

export const getAvailableChalesForPeriod = async (
  checkInDate,
  checkOutDate,
  excludeReservationId = null,
) => {
  const allChaletNumbers = Array.from(
    { length: NUMBER_OF_CHALES },
    (_, i) => i + 1,
  );

  if (!checkInDate || !checkOutDate || checkOutDate <= checkInDate) {
    return allChaletNumbers;
  }

  const q = query(
    collection(db, "reservations"),
    where("checkInDate", "<", checkOutDate),
    where("checkOutDate", ">", checkInDate),
  );

  const querySnapshot = await getDocs(q);
  const occupiedChaletNumbers = new Set();
  querySnapshot.forEach((doc) => {
    if (doc.id !== excludeReservationId) {
      occupiedChaletNumbers.add(doc.data().chaletNumber);
    }
  });

  const availableChales = allChaletNumbers.filter(
    (num) => !occupiedChaletNumbers.has(num),
  );
  return availableChales;
};

export const useChalesData = (selectedDate, searchTerm) => {
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    let q;

    if (searchTerm && searchTerm.length > 3) {
      // Logic for Global Search (Search Mode)
      // Note: Firestore is case-sensitive. "phoneInfo" usually contains text/numbers.
      // We implement a prefix search: startAt(term), endAt(term + '\uf8ff')

      q = query(
        collection(db, "reservations"),
        where("phoneInfo", ">=", searchTerm),
        where("phoneInfo", "<=", searchTerm + "\uf8ff"),
      );
    } else {
      // Logic for Date Filtering (Standard Mode)
      if (!selectedDate) {
        // Should not happen usually as default is today, but safe-guard
        setIsLoading(false);
        return;
      }

      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      q = query(
        collection(db, "reservations"),
        where("checkInDate", "<=", endOfDay),
        where("checkOutDate", ">=", startOfDay),
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedReservations = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedReservations.push({
            id: doc.id,
            ...data,
            checkInDate: data.checkInDate?.toDate(),
            checkOutDate: data.checkOutDate?.toDate(),
            depositDueDate: data.depositDueDate?.toDate(),
          });
        });
        setReservations(fetchedReservations);
        setIsLoading(false);
      },
      (snapshotError) => {
        console.error(
          "Erro no listener do Firestore (Reservations):",
          snapshotError,
        );
        setError("Não foi possível carregar as reservas.");
        setIsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [selectedDate, searchTerm]);

  const chales = useMemo(() => {
    const allChales = Array.from({ length: NUMBER_OF_CHALES }, (_, i) => ({
      chaletNumber: i + 1,
      isAvailable: true,
      guestInfo: "Disponível",
      payments: [],
      totalPaid: 0,
      reservationId: null,
    }));
    reservations.forEach((res) => {
      const chaletIndex = allChales.findIndex(
        (c) => c.chaletNumber === res.chaletNumber,
      );
      if (chaletIndex !== -1) {
        const existingPayments = Array.isArray(res.payments)
          ? res.payments
          : [];
        const initialDepositValue = cleanValue(res.initialDeposit);
        let finalPayments = [...existingPayments];
        const hasDepositInList = existingPayments.some(
          (p) => p.type === "Depósito",
        );
        if (initialDepositValue > 0 && !hasDepositInList) {
          finalPayments.unshift({
            id: `deposito_${res.id}`,
            type: "Depósito",
            value: initialDepositValue,
          });
        }
        const totalPaid = finalPayments.reduce(
          (sum, p) => sum + (p.value || 0),
          0,
        );
        allChales[chaletIndex] = {
          ...allChales[chaletIndex],
          isAvailable: false,
          guestInfo: res.guestInfo,
          phoneInfo: res.phoneInfo,
          checkInDate: res.checkInDate,
          checkOutDate: res.checkOutDate,
          initialDeposit: res.initialDeposit,
          payments: finalPayments,
          totalPaid: totalPaid,
          reservationId: res.id,
          responsibleName: res.responsibleName || "",
          cpf: res.cpf || "",
          address: res.address || "",
          guestList: res.guestList || "",
          depositDueDate: res.depositDueDate,
          observations: res.observations || "",
        };
      }
    });
    return allChales;
  }, [reservations]);

  const checkAndUseCredit = async (phone, checkInDate) => {
    const cleanPhone = phone.replace(/\D/g, "");
    if (!cleanPhone) return null;

    const creditRef = doc(db, "credits", cleanPhone);
    const creditSnap = await getDoc(creditRef);

    if (creditSnap.exists()) {
      const creditData = creditSnap.data();
      const expirationDate = creditData.expirationDate.toDate();
      const checkIn = new Date(checkInDate);

      if (checkIn <= expirationDate) {
        return {
          value: creditData.value,
          ref: creditRef,
        };
      }
    }
    return null;
  };

  const addPayment = async (reservationId, payment) => {
    if (!reservationId) return toast.error("ID da reserva não encontrado.");
    const docRef = doc(db, "reservations", reservationId);
    try {
      await updateDoc(docRef, {
        payments: arrayUnion({ ...payment, id: Date.now() }),
        paymentStatus: "paid",
      });
      toast.success("Pagamento adicionado.");
    } catch (err) {
      toast.error("Erro ao adicionar pagamento.");
      console.error(err);
    }
  };

  const removePayment = async (reservationId, paymentObject) => {
    if (!reservationId) return toast.error("ID da reserva não encontrado.");
    const docRef = doc(db, "reservations", reservationId);
    try {
      await updateDoc(docRef, {
        payments: arrayRemove(paymentObject),
      });
      toast.success("Pagamento removido.");
    } catch (err) {
      toast.error("Erro ao remover pagamento.");
      console.error(err);
    }
  };

  const addReservation = async (data) => {
    try {
      let status = data.depositDueDate ? "pending" : null;
      let initialPayments = [];

      if (data.phoneInfo && data.checkInDate) {
        const creditInfo = await checkAndUseCredit(
          data.phoneInfo,
          data.checkInDate,
        );

        if (creditInfo) {
          initialPayments.push({
            id: Date.now(),
            type: "Crédito",
            value: creditInfo.value,
          });
          status = "paid";

          await deleteDoc(creditInfo.ref);
          toast.success(
            `Crédito de R$ ${creditInfo.value} aplicado automaticamente!`,
          );
        }
      }

      await addDoc(collection(db, "reservations"), {
        ...data,
        initialDeposit: 0,
        paymentStatus: status,
        payments: initialPayments,
        createdAt: Timestamp.now(),
        source: "app_manual",
      });
      toast.success("Reserva adicionada com sucesso!");
    } catch (err) {
      toast.error(`Falha ao adicionar reserva: ${err.message}`);
      console.error(err);
    }
  };

  const deleteReservation = async (reservationId) => {
    if (!reservationId) return;
    try {
      await deleteDoc(doc(db, "reservations", reservationId));
      toast.success("Reserva cancelada com sucesso.");
      return true;
    } catch (err) {
      toast.error(`Falha ao cancelar reserva: ${err.message}`);
      console.error(err);
      return false;
    }
  };

  const updateReservation = async (reservationId, data) => {
    if (!reservationId) return toast.error("ID da reserva não encontrado.");
    const docRef = doc(db, "reservations", reservationId);
    try {
      const currentRes = reservations.find((r) => r.id === reservationId);
      const hasExistingPayments = currentRes?.payments?.length > 0;
      const hasInitialDeposit = cleanValue(currentRes?.initialDeposit) > 0;

      const isAlreadyPaid = hasExistingPayments || hasInitialDeposit;

      if (isAlreadyPaid) {
        data.paymentStatus = "paid";
      } else if (data.depositDueDate) {
        data.paymentStatus = "pending";
      } else {
        data.paymentStatus = null;
      }

      await updateDoc(docRef, data);
      toast.success("Reserva atualizada com sucesso!");
    } catch (err) {
      toast.error(`Falha ao atualizar reserva: ${err.message}`);
      console.error(err);
    }
  };

  const addCredit = async (creditData) => {
    try {
      const cleanPhone = creditData.phone.replace(/\D/g, "");
      if (!cleanPhone) throw new Error("Telefone inválido.");

      const docRef = doc(db, "credits", cleanPhone);

      await setDoc(docRef, {
        clientName: creditData.name,
        phone: creditData.phone,
        value: parseFloat(creditData.value),
        expirationDate: Timestamp.fromDate(
          new Date(creditData.expirationDate + "T00:00:00"),
        ),
        createdAt: Timestamp.now(),
        sourceReservationId: creditData.sourceReservationId || "",
        status: "active",
      });

      toast.success("Crédito gerado com sucesso!");
      return true;
    } catch (err) {
      console.error("Erro ao gerar crédito:", err);
      toast.error("Erro ao salvar o crédito.");
      return false;
    }
  };

  const totals = useMemo(() => {
    return chales.reduce(
      (acc, curr) => {
        const isSameDate = (d1, d2) => {
          if (!d1 || !d2) return false;
          const date1 = new Date(d1);
          const date2 = new Date(d2);
          return date1.getFullYear() === date2.getFullYear() &&
                 date1.getMonth() === date2.getMonth() &&
                 date1.getDate() === date2.getDate();
        };

        if (curr.payments && Array.isArray(curr.payments) && isSameDate(curr.checkInDate, selectedDate)) {
          curr.payments.forEach((p) => {
            const type = p.type.toLowerCase();

            let key = type;
            if (
              type === "deposito" ||
              type === "depósito" ||
              type === "crédito" ||
              type === "credito"
            ) {
              key = "depósito";
            }

            if (acc[key] !== undefined) {
              acc[key] += p.value;
            }
          });
        }
        return acc;
      },
      { depósito: 0, pix: 0, cartão: 0, dinheiro: 0 },
    );
  }, [chales, selectedDate]);

  const launchFinancials = async () => {
    setIsLaunching(true);
    setError(null);
    try {
      const dateStr = formatDateToYYYYMMDD(selectedDate);
      const chalesTotal = totals.depósito + totals.pix + totals.cartão + totals.dinheiro;
      
      const docRef = doc(db, "financial_history", dateStr);
      const docSnap = await getDoc(docRef);
      
      let totalGeral = chalesTotal;
      if (docSnap.exists()) {
        const data = docSnap.data();
        totalGeral += (data.entradas || 0) + (data.brinquedos || 0);
      }

      await setDoc(docRef, {
        date: dateStr,
        chales: {
          depósito: totals.depósito,
          pix: totals.pix,
          cartão: totals.cartão,
          dinheiro: totals.dinheiro,
          total: chalesTotal,
        },
        total_geral: totalGeral
      }, { merge: true });

      toast.success("Caixa de chalés lançado com sucesso no histórico!");
    } catch (err) {
      setError(err.message);
      toast.error(`Erro ao lançar: ${err.message}`);
    } finally {
      setIsLaunching(false);
    }
  };

  return {
    chales,
    isLoading,
    error,
    addPayment,
    removePayment,
    totals,
    launchFinancials,
    isLaunching,
    addReservation,
    deleteReservation,
    updateReservation,
    addCredit,
  };
};
